import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_URL,
});

// 요청 인터셉터 - 토큰 추가
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 응답 인터셉터 - 401 처리
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // 로그인 요청이나 백그라운드 API는 로그아웃 처리하지 않음
      const skipLogout = url.includes('/auth/') || url.includes('/unread');
      if (!skipLogout && localStorage.getItem('token')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Orders API
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getCompanies: () => api.get('/orders/meta/companies'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  assign: (orderId, driverId, data) => api.post(`/orders/${orderId}/assign/${driverId}`, data),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
  getDashboardStats: () => api.get('/orders/stats/dashboard'),
  confirmPayment: (id) => api.post(`/orders/${id}/confirm-payment`),
  proposePrice: (id, client_offered_price) => api.put(`/orders/${id}/propose-price`, { client_offered_price }),
  notifyPrice: (id, client_offered_price) => api.put(`/orders/${id}/notify-price`, { client_offered_price }),
  updatePriceStatus: (id, price_status) => api.put(`/orders/${id}/price-status`, { price_status }),
};

// Broadcast API
export const broadcastAPI = {
  broadcast: (orderId, data) => api.post(`/broadcast/${orderId}/broadcast`, data),
  cancelBroadcast: (orderId) => api.post(`/broadcast/${orderId}/cancel-broadcast`),
  getApplications: (orderId) => api.get(`/broadcast/${orderId}/applications`),
  approveApplication: (orderId, applicationId) =>
    api.post(`/broadcast/${orderId}/applications/${applicationId}/approve`),
  cancelAssignment: (orderId) => api.delete(`/broadcast/${orderId}/assignment`),
};

// Drivers API
export const driversAPI = {
  getAll: (params) => api.get('/drivers', { params }),
  getDeleted: () => api.get('/drivers', { params: { include_deleted: 'true' } }),
  getById: (id) => api.get('/drivers/' + id),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put('/drivers/' + id, data),
  delete: (id) => api.delete('/drivers/' + id),
  permanentDelete: (id) => api.delete('/drivers/' + id + '/permanent'),
  restore: (id) => api.put('/drivers/' + id + '/restore'),
  approve: (id) => api.put('/drivers/' + id + '/approve'),
  toggleActive: (id) => api.put('/drivers/' + id + '/toggle-active'),
  changePassword: (id, newPassword) => api.put('/drivers/' + id + '/change-password', { new_password: newPassword }),
  updateRating: (id, rating) => api.put('/drivers/' + id + '/rating', { rating }),
  getAuditLog: (params) => api.get('/drivers/audit/log', { params }),
  downloadAuditLog: () => api.get('/drivers/audit/download', { responseType: 'blob' }),
};

// Clients API (B2B 발주업체 — 레거시)
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Customers (회원가입 고객) — 직원 포털 관리용
export const customersAdminAPI = {
  getAll: (params) => api.get('/admin/customers', { params }),
  getById: (id) => api.get(`/admin/customers/${id}`),
  update: (id, data) => api.put(`/admin/customers/${id}`, data),
  changePassword: (id, new_password) => api.put(`/admin/customers/${id}/password`, { new_password }),
  getOrders: (id) => api.get(`/admin/customers/${id}/orders`),
  lookupByBusinessNumber: (bn) => api.get(`/admin/customers/lookup/by-business-number/${encodeURIComponent(bn)}`),
  findOrCreate: (data) => api.post('/admin/customers/find-or-create', data),
  search: (q) => api.get('/admin/customers/search/quick', { params: { q } }),
};

// 거래실적 (직원 포털)
export const transactionsAdminAPI = {
  getAll: (params) => api.get('/admin/transactions', { params }),
  getFilters: () => api.get('/admin/transactions/filters'),
};

// Staff API
export const staffAPI = {
  getAll: () => api.get('/staff'),
  getStats: () => api.get('/staff/stats/summary'),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  updateStatus: (id, data) => api.put(`/staff/${id}/status`, data),
  changePassword: (id, data) => api.put(`/staff/${id}/password`, data),
};

// Location API
export const locationAPI = {
  getDriverLocations: () => api.get('/location/drivers'),
};

// Staff Notifications API
export const staffNotificationsAPI = {
  getAll: (params) => api.get('/staff-notifications', { params }),
  getUnreadCount: () => api.get('/staff-notifications/unread-count'),
  getArchivedCount: () => api.get('/staff-notifications/archived-count'),
  markRead: (id) => api.patch(`/staff-notifications/${id}/read`),
  markAllRead: (direction) => api.patch('/staff-notifications/read-all', direction ? { direction } : {}),
  // delete: 보관함 이동 (soft delete)
  delete: (id) => api.delete(`/staff-notifications/${id}`),
  restore: (id) => api.patch(`/staff-notifications/${id}/restore`),
};

// Admin API (시스템 설정)
export const adminAPI = {
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
};

// Event Logs API
export const eventLogsAPI = {
  getAll: (params) => api.get('/event-logs', { params }),
  getEventTypes: () => api.get('/event-logs/event-types'),
};

// Messages API
export const messagesAPI = {
  getMessages: (orderId) => api.get(`/messages/${orderId}`),
  sendMessage: (orderId, message) => api.post(`/messages/${orderId}`, { message }),
  getUnreadCount: (orderId) => api.get(`/messages/${orderId}/unread`),
  getUnreadAll: () => api.get('/messages/staff/unread-all'),
};

export default api;
